"use client";

import { useState, useCallback, useEffect } from "react";


import { z } from "zod";
import RatingScale from "../rating-scale";
import ReactCrudForm, { FormTextarea } from "../form";
import { ReactFormField, ReactFormUpdate } from "@7span/react-form";
import { Button } from "ui/components/button";
import { readItem, updateItem } from "@directus/sdk";
import LoadingSkeleton from "./loader";
import { DetailItem } from "../details/item";
import { UserDisplay } from "../user/display";
import { formatDateForDisplay } from "components-v2/utils";
import HeaderPortal from "components-v2/portal/header";
import ButtonWithLoading from "components/button-with-loading";

export default function SurveyFeedbackForm({
  itemId,
  client,
  onSuccess,
  readMode = false,
  clientMode = true,
  amUpdateMode = false,
  amReadMode = false,
  onDataLoad,
  userId,
}) {
  const [surveyData, setSurveyData] = useState(null);
  const [schema, setSchema] = useState(() => z.object({}));

  const readSurvey = useCallback(async (id) => {
    try {
      const fields = [
        "id",
        "title",
        "feedback",
        "questions.id",
        "status",
        "questions.question.id",
        "questions.question.question",
        "questions.question.category",
        "questions.response",
        "date_created",
        "user_created.first_name",
        "user_created.last_name",
        "user_created.avatar",
        ...(!clientMode ? ["note",
          "submitted_on",
          "submitted_by.first_name",
          "submitted_by.last_name",
          "submitted_by.avatar",] : []),
        ...(amUpdateMode ? ["note"] : [])
      ];

      const survey = await client.request(
        readItem("surveys", id, {
          fields,
        }),
      );

      if (!survey) throw new Error("Survey not found");

      setSurveyData(survey);
      if (onDataLoad) {
        onDataLoad(survey);
      }

      // Create dynamic Zod schema based on loaded questions
      const shape = {};
      const values = {};

      survey.questions?.forEach((q) => {
        const fieldName = `question_${q.id}`;
        values[fieldName] = q.response;
        shape[fieldName] = z
          .number({ required_error: "This field is required" })
          .min(1, "This field is required");
      });

      shape["feedback"] = z.string();
      values["feedback"] = survey.feedback || "";

      if (amUpdateMode) {
        shape["note"] = z.string({ required_error: "This field is required" }).min(1, "This field is required");
      } else {
        shape["note"] = z.string().optional();
      }
      values["note"] = survey.note || "";

      setSchema(z.object(shape));

      return { values };
    } catch (err) {
      throw err;
    }
  }, [client, onDataLoad]);

  const submitSurveyResponses = useCallback(
    async (id, context) => {
      try {
        const questionsPayload = surveyData.questions.map((q) => ({
          id: q.id, // Primary key of the junction table row
          response: context.values[`question_${q.id}`],
        }));

        const payload = {
          questions: questionsPayload,
          feedback: context.values.feedback,

        };

        if (amUpdateMode) {
          payload.note = context.values.note;
        }

        if (surveyData.status !== "submitted" && userId) {
          payload.submitted_by = userId;
          payload.submitted_on = new Date().toISOString();
          payload.status = "submitted";
        }

        const query = {
          fields: [
            "id",
            "title",
            "feedback",
            "questions.id",
            "questions.question.id",
            "questions.question.question",
            "questions.question.category",
            "questions.response",
            ...(amUpdateMode ? ["note"] : []),
          ],
        };

        const result = await client.request(
          updateItem("surveys", id, payload, query),
        );

        const updatedValues = {};
        result.questions?.forEach((q) => {
          const fieldName = `question_${q.id}`;
          updatedValues[fieldName] = q.response ? String(q.response) : "";
        });
        updatedValues["feedback"] = result.feedback || "";
        updatedValues["note"] = result.note || "";

        return { values: updatedValues };
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    [client, surveyData],
  );




  return (
    <ReactCrudForm
      itemId={itemId}
      schema={schema}
      read={readSurvey}
      update={submitSurveyResponses}
      actionPortal="none"
      onSuccess={onSuccess}
      className="space-y-6"
      formContainerClassName="w-full"
      successMessage="Feedback submitted successfully!"
    >
      {({ dirtyValues, context }) => {
        if (context?.isReading) {
          return <LoadingSkeleton />;
        }
        const isStatusSubmitted =
          readMode ||
          surveyData?.status === "submitted";


        return (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className={`space-y-4 ${!clientMode ? "md:col-span-8" : "md:col-span-12"}`}>
              {surveyData?.questions?.map((q) => {
                return (
                  <ReactFormField key={q.id} name={`question_${q.id}`}>
                    {({ value, field, error }) => (
                      <RatingScale
                        question={q.question?.question}
                        value={value}
                        onChange={field.onChange}
                        error={
                          error?.message ||
                          (error ? "This field is required" : null)
                        }
                        disabled={isStatusSubmitted}
                      />
                    )}
                  </ReactFormField>
                );
              })}

              <ReactFormField name="feedback">
                {({ value, field, error }) => (
                  <FormTextarea
                    {...field}
                    value={value || ""}
                    labelClassName="text-base font-semibold text-zinc-950  leading-snug"
                    label="How would you like to describe your experience collaborating with us in a few words."
                    error={error}
                    placeholder="Write Here"
                    className="min-h-32"
                    readOnly={isStatusSubmitted}
                  />
                )}
              </ReactFormField>

              {(amUpdateMode || amReadMode) && (
                <ReactFormField name="note">
                  {({ value, field, error }) => (
                    <FormTextarea
                      {...field}
                      value={value || ""}
                      labelClassName="text-base font-semibold text-zinc-950  leading-snug"
                      label="Internal Note (Visible only to your team)"
                      error={error}
                      placeholder="Write Here"
                      className="min-h-32"
                      readOnly={amReadMode}
                    />
                  )}
                </ReactFormField>
              )}

              {!isStatusSubmitted && (
                <div className="mt-8 flex justify-start">
                  <ReactFormUpdate>
                    {({ updateItem, isUpdating, isLoading }) => (
                      <Button
                        onClick={updateItem}
                        disabled={
                          isUpdating ||
                          isLoading ||
                          !dirtyValues ||
                          Object.keys(dirtyValues).length === 0
                        }
                        className="bg-zinc-950 text-white hover:bg-zinc-900 px-8 py-3 rounded-lg font-medium text-sm transition-colors duration-200 shadow-sm cursor-pointer disabled:opacity-50"
                      >
                        {isUpdating ? "Submitting..." : "Submit"}
                      </Button>
                    )}
                  </ReactFormUpdate>
                </div>
              )}

              {(amUpdateMode && isStatusSubmitted) && (
                <HeaderPortal>
                  <ReactFormUpdate>
                    {({ updateItem, isUpdating, isLoading }) => (
                      <ButtonWithLoading
                        variant="default"
                        onClick={updateItem}
                        isLoading={isLoading}
                        disabled={
                          isLoading || isUpdating || !dirtyValues || Object.keys(dirtyValues).length === 0
                        }
                      >
                        Update
                      </ButtonWithLoading>
                    )}
                  </ReactFormUpdate>
                </HeaderPortal>
              )}
            </div>

            {amReadMode && (
              <div className="md:col-span-4 border rounded-lg bg-background overflow-hidden px-4 py-3">

                <DetailItem label="Created By">
                  <UserDisplay user={surveyData?.user_created} />
                </DetailItem>


                <DetailItem label="Created On">
                  {formatDateForDisplay(surveyData?.date_created)}
                </DetailItem>


                <DetailItem label="Submitted By">
                  {surveyData?.submitted_by ? <UserDisplay user={surveyData.submitted_by} /> : "-"}
                </DetailItem>


                <DetailItem label="Submitted On" showSeparator={false}>
                  {formatDateForDisplay(surveyData?.submitted_on)}
                </DetailItem>

              </div>
            )}
          </div>
        );
      }}
    </ReactCrudForm>
  );
}
